import { z } from "zod";
import { refineScenarioStructure } from "./scenario-structure-refine.js";

export const ScenarioBaseSchema = z.enum(["CURRENT_LINE", "SAVED_VERSION", "DUPLICATE_SIM"]);

export const CreateScenarioRequestSchema = z.object({
  // Ausente ⇒ "linha nova" (cenário montado só do acervo, sem linha base).
  lineId: z.string().uuid().optional(),
  base: ScenarioBaseSchema.default("CURRENT_LINE"),
  name: z.string().min(1).max(120),
  notes: z.string().max(1000).optional(),
  // Com `lineId` + `productId`, o cenário JÁ NASCE com `structureSnap` derivado da
  // linha, e portanto executável sem que ninguém abra o sandbox. É o que permite o botão Simular
  // do hub entregar resposta direto. Ausente ⇒ snapshot nulo e o comportamento anterior.
  productId: z.string().uuid().optional(),
});
export type CreateScenarioRequest = z.infer<typeof CreateScenarioRequestSchema>;

export const MachineOverrideSchema = z.object({
  machineId: z.string().uuid(),
  field: z.string().min(1),
  value: z.any().refine((v) => v !== undefined, { message: "value é obrigatório" }),
});

export const ApplyOverridesRequestSchema = z.object({
  overrides: z.array(MachineOverrideSchema).min(1).max(200),
});
export type ApplyOverridesRequest = z.infer<typeof ApplyOverridesRequestSchema>;

// ── Parâmetros de simulação por máquina ───────────────────────────────────────
// Os 5 tipos tipados do builder do protótipo (user_spec/ajuste-opus-dt). Vivem no
// snapshot do cenário (Scenario.machineOverrides[].simProfile/simOverrides); a autoria
// (UI do builder + gate de validação) é a Spec 1b. Em 1a a proveniência é sempre "manual".
export const SimParamSchema = z.enum(["feedRate", "cycleTime", "mtbf", "mttr", "quality"]);
export type SimParam = z.infer<typeof SimParamSchema>;

// `product` = valor autorado no par (máquina×produto) — vence premissa/similar,
// perde para o medido do próprio produto (research D4/D9).
export const SimProvenanceSchema = z.enum(["manual", "measured", "similar", "spec", "product"]);
export type SimProvenance = z.infer<typeof SimProvenanceSchema>;

// ── Proveniência POR PARÂMETRO ─────────────────────────────────────────────────
// Params que admitem origem medida. `cycleTime` EXCLUÍDO de propósito: ciclo é derivado
// (3600/feedRate) e HERDA a origem de feedRate.
export const SimMeasurableParamSchema = z.enum(["feedRate", "quality", "mtbf", "mttr"]);
export type SimMeasurableParam = z.infer<typeof SimMeasurableParamSchema>;
/** Fonte ÚNICA da lista de params deriváveis — evita cópias divergentes em api/web. */
export const MEASURABLE_PARAMS: readonly SimMeasurableParam[] = SimMeasurableParamSchema.options;

/** Origem + evidência de UM parâmetro. `sampleCount` = nº de paradas (mtbf/mttr) ou
 * unidades produzidas na janela (feedRate/quality). */
export const ParamProvenanceSchema = z.object({
  provenance: SimProvenanceSchema,
  sampleCount: z.number().int().nonnegative().optional(),
});
export type ParamProvenance = z.infer<typeof ParamProvenanceSchema>;

/** Mapa parcial param → origem (objeto explícito, não z.record: Zod 3 inferiria Record completo). */
export const ProvenanceByParamSchema = z.object({
  feedRate: ParamProvenanceSchema.optional(),
  quality: ParamProvenanceSchema.optional(),
  mtbf: ParamProvenanceSchema.optional(),
  mttr: ParamProvenanceSchema.optional(),
});
export type ProvenanceByParam = z.infer<typeof ProvenanceByParamSchema>;

/** Baseline manual dos 5 params por máquina. Campos ausentes caem no fallback
 * conservador do worker, sempre marcados como premissa (sem dado). */
export const SimProfileSchema = z.object({
  feedRateUnH: z.number().positive().optional(),
  cycleTimeS: z.number().positive().optional(),
  mtbfMin: z.number().positive().optional(),
  mttrMin: z.number().positive().optional(),
  qualityTargetPct: z.number().gt(0).max(100).optional(),
  provenance: SimProvenanceSchema.default("manual"),
  // Nº de paradas que sustentam a derivação medida de MTBF/MTTR. Alimenta o
  // cap de confiança do motor (poucas amostras ⇒ peso rebaixado) e sobrevive ao snapshot salvo.
  sampleCount: z.number().int().nonnegative().optional(),
  // Origem POR parâmetro (feed/quality/mtbf/mttr podem estar em tiers
  // diferentes). Ausente ⇒ snapshot legado: fallback do escalar acima via originOf()
  // (lib/sim-provenance.ts) — o escalar só projeta em mtbf/mttr.
  provenanceByParam: ProvenanceByParamSchema.optional(),
});
export type SimProfile = z.infer<typeof SimProfileSchema>;

/** Override tipado por parâmetro de simulação; variação limitada a ±25%. */
export const SimOverrideSchema = z.object({
  param: SimParamSchema,
  deltaPct: z.number().min(-25).max(25),
});
export type SimOverride = z.infer<typeof SimOverrideSchema>;

// ── Autoria dos params/overrides por máquina ──────────────────────────────────
// Persiste em Scenario.machineOverrides[].simProfile/.simOverrides (o motor de 1a já
// lê de lá). Idempotente por máquina; ≤1 override por param (último vence).
export const SimMachineEntrySchema = z.object({
  machineId: z.string().uuid(),
  simProfile: SimProfileSchema.optional(),
  simOverrides: z.array(SimOverrideSchema).max(5).optional(),
});
export type SimMachineEntry = z.infer<typeof SimMachineEntrySchema>;

export const SetScenarioSimMachinesRequestSchema = z.object({
  machines: z.array(SimMachineEntrySchema).min(1).max(200),
});
export type SetScenarioSimMachinesRequest = z.infer<typeof SetScenarioSimMachinesRequestSchema>;

// ── Snapshot estrutural do cenário (sandbox "e se…") ───────────────────────────
// Cópia auto-contida da linha (máquinas presentes/adicionadas + topologia) que o
// worker resolve para o motor sem tocar a linha real. Reusa SimProfile/SimOverride.
// Validado por refinements da mesma família de draft-line-refinements.

// ── Modelo em camadas: estágios são a fonte de autoria; edges derivadas no save ─────────────
/** Estágio (camada) do pipeline. `key` é id local estável; `name`/`order` editáveis. */
export const SandboxStageSchema = z.object({
  key: z.string().min(1).max(40),
  name: z.string().min(1).max(60),
  order: z.number().int().min(0),
});
export type SandboxStage = z.infer<typeof SandboxStageSchema>;

/** Nó de máquina dentro do snapshot estrutural. `key` é o id local estável no cenário;
 * `sourceMachineId` rastreia a Machine real de origem (linha ou acervo — Princípio IV);
 * `stageKey` referencia o `SandboxStage.key` do estágio a que pertence. */
export const ScenarioMachineEntrySchema = z.object({
  key: z.string().min(1).max(40),
  sourceMachineId: z.string().uuid(),
  origin: z.enum(["line", "acervo"]),
  present: z.boolean().default(true), // false = desligada/removida do cenário
  stageKey: z.string().min(1).max(40), // Referencia SandboxStage.key
  simProfile: SimProfileSchema.optional(),
  simOverrides: z.array(SimOverrideSchema).max(5).optional(),
});
export type ScenarioMachineEntry = z.infer<typeof ScenarioMachineEntrySchema>;

export const ScenarioEdgeSchema = z.object({
  sourceKey: z.string(),
  targetKey: z.string(),
  type: z.literal("PRODUCTION_FLOW").default("PRODUCTION_FLOW"),
});
export type ScenarioEdge = z.infer<typeof ScenarioEdgeSchema>;

export const ScenarioCanvasNodeSchema = z.object({
  key: z.string(),
  x: z.number(),
  y: z.number(),
  nodeType: z.enum(["MACHINE", "START", "END"]),
});
export type ScenarioCanvasNode = z.infer<typeof ScenarioCanvasNodeSchema>;

export const ScenarioStructureSchema = z
  .object({
    machines: z.array(ScenarioMachineEntrySchema).max(200),
    stages: z.array(SandboxStageSchema).max(40), // Edges derivadas destes no save
    edges: z.array(ScenarioEdgeSchema).max(400),
    canvas: z.object({ nodes: z.array(ScenarioCanvasNodeSchema) }),
    productId: z.string().uuid(),
    baseLineId: z.string().uuid().nullable(), // null = linha nova
  })
  .superRefine(refineScenarioStructure);
export type ScenarioStructure = z.infer<typeof ScenarioStructureSchema>;

export const UpdateScenarioStructureRequestSchema = z.object({
  structure: ScenarioStructureSchema,
  baseUpdatedAt: z.string().datetime().optional(), // âncora de detecção de edição concorrente
});
export type UpdateScenarioStructureRequest = z.infer<typeof UpdateScenarioStructureRequestSchema>;

export const UpdateScenarioStructureResponseSchema = z.object({
  id: z.string().uuid(),
  updatedAt: z.string().datetime(),
  concurrentEdit: z.boolean(), // true se updatedAt > baseUpdatedAt na chegada
  lineDrift: z.boolean(), // true se a linha base mudou desde a criação
});
export type UpdateScenarioStructureResponse = z.infer<typeof UpdateScenarioStructureResponseSchema>;

// ── Listagem de cenários salvos para o hub ───────────────────────────────
export const ScenarioListItemSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  lineId: z.string().uuid().nullable(), // null = linha nova
  lineName: z.string().nullable(),
  updatedAt: z.string().datetime(),
  lastRun: z
    .object({
      simulationId: z.string().uuid(),
      status: z.enum(["QUEUED", "RUNNING", "COMPLETED", "FAILED"]),
      oee: z.number().finite().nullable(),
      confidenceLevel: z.enum(["HIGH", "MEDIUM", "LOW", "INSUFFICIENT"]).nullable(),
      completedAt: z.string().datetime().nullable(),
    })
    .nullable(), // cenário sem rodada ainda
  // Colunas do hub: período da última rodada + Δ OEE vs. baseline do cenário.
  periodLabel: z.string().nullable(),
  deltaOee: z.number().finite().nullable(),
});
export type ScenarioListItem = z.infer<typeof ScenarioListItemSchema>;

export const ListScenariosResponseSchema = z.object({
  items: z.array(ScenarioListItemSchema),
  total: z.number().int(),
});
export type ListScenariosResponse = z.infer<typeof ListScenariosResponseSchema>;
